import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  Check,
  Building2,
  Sparkles,
  ArrowRight,
  UserCheck,
} from "lucide-react";
import { PortableText } from "@portabletext/react";
import { getPostBySlug, urlFor } from "../sanityClient";

export default function BlogPost({ slug, onBackToBlog, onViewDashboard }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function fetchPost() {
      try {
        setLoading(true);
        const data = await getPostBySlug(slug);
        if (isMounted) {
          if (!data) {
            setError("Article not found.");
          } else {
            setPost(data);
            setError(null);
          }
        }
      } catch (err) {
        console.error("Failed to load article:", err);
        if (isMounted) setError(err.message || "Failed to load article.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    if (slug) {
      fetchPost();
    }
    return () => {
      isMounted = false;
    };
  }, [slug]);

  // Inject JSON-LD Schema for SEO / AEO / GEO
  useEffect(() => {
    if (!post) return;

    // Update browser title
    document.title = `${post.title} | Casino Figures Insights`;

    // Build JSON-LD Article Schema
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt || post.title,
      image: post.mainImage ? urlFor(post.mainImage)?.url() : undefined,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      author: {
        "@type": "Person",
        name: post.author?.name || "Casino Figures Analyst",
        description: post.author?.bio || "Commercial gaming and casino revenue analyst",
      },
      publisher: {
        "@type": "Organization",
        name: "Casino Figures",
        logo: {
          "@type": "ImageObject",
          url: "https://casinofigures.com/logo.png",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": window.location.href,
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.title = "Casino Figures - Gaming Industry Intelligence";
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [post]);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // Custom Portable Text Renderers for Tailwind Styling
  const portableTextComponents = {
    types: {
      image: ({ value }) => {
        if (!value?.asset) return null;
        return (
          <figure className="my-8 rounded-2xl overflow-hidden border border-gray-800 bg-gray-900/60">
            <img
              src={urlFor(value).width(1200).url()}
              alt={value.alt || "Article illustration"}
              className="w-full object-cover max-h-[500px]"
              loading="lazy"
            />
            {value.caption && (
              <figcaption className="p-3 text-center text-xs text-gray-400 border-t border-gray-800">
                {value.caption}
              </figcaption>
            )}
          </figure>
        );
      },
    },
    block: {
      h2: ({ children }) => (
        <h2 className="text-2xl sm:text-3xl font-bold text-white mt-10 mb-4 tracking-tight border-b border-gray-800/80 pb-2">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-xl sm:text-2xl font-semibold text-purple-300 mt-8 mb-3 tracking-tight">
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-lg font-semibold text-gray-200 mt-6 mb-2">
          {children}
        </h4>
      ),
      normal: ({ children }) => (
        <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 font-normal">
          {children}
        </p>
      ),
      blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-purple-500 pl-5 py-2 my-6 italic text-gray-300 bg-purple-950/10 rounded-r-xl">
          {children}
        </blockquote>
      ),
    },
    list: {
      bullet: ({ children }) => (
        <ul className="list-disc list-inside space-y-2 text-gray-300 mb-6 text-base sm:text-lg pl-2">
          {children}
        </ul>
      ),
      number: ({ children }) => (
        <ol className="list-decimal list-inside space-y-2 text-gray-300 mb-6 text-base sm:text-lg pl-2">
          {children}
        </ol>
      ),
    },
    marks: {
      strong: ({ children }) => (
        <strong className="font-semibold text-white">{children}</strong>
      ),
      em: ({ children }) => <em className="italic text-gray-200">{children}</em>,
      code: ({ children }) => (
        <code className="bg-gray-800 text-purple-300 px-1.5 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      ),
      link: ({ value, children }) => {
        const target = (value?.href || "").startsWith("http") ? "_blank" : undefined;
        return (
          <a
            href={value?.href}
            target={target}
            rel={target === "_blank" ? "noopener noreferrer" : undefined}
            className="text-purple-400 hover:text-purple-300 underline underline-offset-4 decoration-purple-500/50 hover:decoration-purple-400 transition-colors"
          >
            {children}
          </a>
        );
      },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-gray-400">
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm">Loading article...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-4 text-center">
        <p className="text-red-400 text-lg font-semibold mb-2">{error || "Article not found"}</p>
        <button
          onClick={onBackToBlog}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm hover:bg-gray-800 transition-all mt-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to all articles
        </button>
      </div>
    );
  }

  const imageUrl = post.mainImage ? urlFor(post.mainImage)?.width(1400)?.height(700)?.url() : null;
  const formattedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-100">
      {/* Top Navbar */}
      <nav className="border-b border-gray-800/80 bg-gray-950/70 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            onClick={onBackToBlog}
            className="inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>All Articles</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-gray-900 border border-gray-800 text-gray-300 hover:text-white hover:bg-gray-800 transition-all"
              title="Copy article link"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-green-400" />
                  <span className="text-green-400">Copied</span>
                </>
              ) : (
                <>
                  <Share2 className="h-3.5 w-3.5" />
                  <span>Share</span>
                </>
              )}
            </button>
            <button
              onClick={onViewDashboard}
              className="px-3.5 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-semibold rounded-lg hover:shadow-lg hover:shadow-purple-500/25 transition-all"
            >
              Dashboard
            </button>
          </div>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Category & Date */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mb-4">
          {post.categories && post.categories[0] && (
            <span className="uppercase tracking-wider font-semibold text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2.5 py-1 rounded-md">
              {post.categories[0]}
            </span>
          )}
          {formattedDate && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-gray-500" />
              {formattedDate}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-gray-500" />
            5 min read
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Excerpt / AEO Quick Summary Callout */}
        {post.excerpt && (
          <div className="p-5 rounded-2xl bg-gradient-to-r from-purple-950/30 to-blue-950/20 border border-purple-500/30 mb-8">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-purple-300 uppercase tracking-wider mb-2">
              <Sparkles className="h-3.5 w-3.5 text-purple-400" />
              Executive Summary & Key Takeaways
            </div>
            <p className="text-base text-gray-200 leading-relaxed font-medium">
              {post.excerpt}
            </p>
          </div>
        )}

        {/* Author Bio Bar (E-E-A-T) */}
        <div className="flex items-center gap-3 py-4 border-y border-gray-800/80 mb-8">
          {post.author?.image ? (
            <img
              src={urlFor(post.author.image)?.width(96)?.height(96)?.url()}
              alt={post.author.name}
              className="w-11 h-11 rounded-full object-cover border border-purple-500/30"
            />
          ) : (
            <div className="w-11 h-11 rounded-full bg-purple-500/20 text-purple-300 text-sm font-bold flex items-center justify-center border border-purple-500/30">
              {post.author?.name ? post.author.name.charAt(0) : "C"}
            </div>
          )}
          <div>
            <div className="text-sm font-semibold text-white flex items-center gap-1.5">
              <span>{post.author?.name || "Casino Figures Analyst"}</span>
              <UserCheck className="h-3.5 w-3.5 text-purple-400" title="Verified Gaming Analyst" />
            </div>
            <p className="text-xs text-gray-400">
              {post.author?.bio || "Gaming Industry Research & Market Analytics"}
            </p>
          </div>
        </div>

        {/* Cover Image */}
        {imageUrl && (
          <div className="mb-10 rounded-2xl overflow-hidden border border-gray-800 shadow-2xl">
            <img
              src={imageUrl}
              alt={post.title}
              className="w-full object-cover max-h-[500px]"
            />
          </div>
        )}

        {/* Article Body */}
        <div className="prose prose-invert max-w-none">
          {post.body ? (
            <PortableText value={post.body} components={portableTextComponents} />
          ) : (
            <p className="text-gray-400 italic">No content available for this report.</p>
          )}
        </div>

        {/* Call to Action: Live Dashboard */}
        <div className="mt-16 p-8 rounded-3xl bg-gradient-to-br from-purple-950/40 via-gray-900 to-blue-950/30 border border-purple-500/30 text-center relative overflow-hidden">
          <div className="max-w-xl mx-auto">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold mb-3">
              <Building2 className="h-3.5 w-3.5" />
              Direct Data Access
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">
              Explore the Data Behind This Report
            </h3>
            <p className="text-sm text-gray-300 mb-6">
              Track real-time commercial casino metrics, multi-jurisdiction slot performance, and monthly gaming trends.
            </p>
            <button
              onClick={onViewDashboard}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 transition-all text-sm"
            >
              <span>Access Live Dashboard</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </article>
    </div>
  );
}
